/** @jsxImportSource @emotion/react */
import { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { Toast } from "primereact/toast";
import { Tooltip } from "primereact/tooltip";
import { confirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  TablaFiltrable
} from "../../../../shared";

// Opciones de frecuencia de refacturación (deben coincidir con el enum del API).
const FRECUENCIA_OPCIONES = [
  { value: "NINGUNA", etiqueta: "Ninguna (facturación única)" },
  { value: "SEMANAL", etiqueta: "Semanal" },
  { value: "MENSUAL", etiqueta: "Mensual" },
  { value: "SEMESTRAL", etiqueta: "Semestral" },
  { value: "ANUAL", etiqueta: "Anual" },
  { value: "BIENAL", etiqueta: "Bienal" },
  { value: "TRIENAL", etiqueta: "Trienal" },
];
const frecuenciaEtiqueta = (valor) =>
  FRECUENCIA_OPCIONES.find((f) => f.value === valor)?.etiqueta || "";

export default function AsignarCertificadoTipo({ actividadTipo, pasos: pasosProp }) {
  const [asignaciones, setAsignaciones] = useState(
    actividadTipo.actividadtipo_x_certificadotipo || []
  );

  // Sidebar crear
  const [sideBarCrear, setSideBarCrear] = useState(false);
  const [loaderCrear, setLoaderCrear] = useState(false);
  const [formCrearKey, setFormCrearKey] = useState(0);

  // Sidebar editar
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEditarKey, setFormEditarKey] = useState(0);

  // Menú contextual
  const [menuSeleccionado, setMenuSeleccionado] = useState(null);
  const menuRef = useRef(null);

  const toast = useRef(null);
  const { actividadTipoId } = useParams();

  // Pasos del tipo de actividad (ordenados), base para configurar el pago por
  // paso a los colaboradores.
  const pasos = [
    ...(pasosProp || actividadTipo.actividadpasoconfig || [])
  ].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

  // Arma las secciones dinámicas de pago por paso, con prefill opcional a partir
  // de los pagos ya guardados de una asignación.
  function seccionesPago(pagosExistentes) {
    if (!pasos.length) return [];
    const secciones = [
      {
        nombre: "Pago a colaboradores por paso",
        descripcion:
          "Monto y moneda que se le paga al colaborador decisor y al colaborador en formación en cada paso. Dejar vacío si no aplica.",
        campos: []
      }
    ];
    pasos.forEach((paso) => {
      const pago = (pagosExistentes || []).find(
        (p) => p.actividadPasoConfigId === paso.id
      );
      secciones.push({
        nombre: paso.nombre,
        campos: [
          {
            nombre: `pago_${paso.id}_decisor`,
            tipoitemformularioid: 14,
            etiqueta: "Decisor",
            respuesta: {
              precio: pago?.montoDecisor ?? null,
              monedaId: pago?.monedaDecisorId ?? null
            }
          },
          {
            nombre: `pago_${paso.id}_formacion`,
            tipoitemformularioid: 14,
            etiqueta: "En formación",
            respuesta: {
              precio: pago?.montoFormacion ?? null,
              monedaId: pago?.monedaFormacionId ?? null
            }
          }
        ]
      });
    });
    return secciones;
  }

  // Separa los campos base de los campos de pago (pago_<pasoId>_<rol>) y arma el
  // array `pagos` que espera el backend. Cada campo de pago es del tipo precio
  // (tipo 14) y emite { precio, monedaId }, que se aplana a las claves del API.
  function separarPagos(campos) {
    const base = {};
    const porPaso = {};
    Object.entries(campos).forEach(([clave, valor]) => {
      const m = clave.match(/^pago_(\d+)_(decisor|formacion)$/);
      if (!m) {
        base[clave] = valor;
        return;
      }
      const pasoId = parseInt(m[1]);
      porPaso[pasoId] = porPaso[pasoId] || { actividadPasoConfigId: pasoId };
      const precio = valor?.precio ?? null;
      const monedaId = valor?.monedaId ?? null;
      if (m[2] === "decisor") {
        porPaso[pasoId].montoDecisor = precio;
        porPaso[pasoId].monedaDecisorId = monedaId;
      } else {
        porPaso[pasoId].montoFormacion = precio;
        porPaso[pasoId].monedaFormacionId = monedaId;
      }
    });
    return { base, pagos: Object.values(porPaso) };
  }

  // Aplana el campo de precio compuesto (tipo 14) a las claves precio/monedaid
  // que espera el backend para la asignación.
  function aplanarPrecioBase(base) {
    const { precio, ...resto } = base;
    return {
      ...resto,
      precio: precio?.precio ?? null,
      monedaid: precio?.monedaId ?? null
    };
  }

  // Formulario de crear asignación
  const crearFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "certificadotipoid",
            tipoitemformularioid: 5,
            etiqueta: "Tipo de certificado",
            opciones: "admin/config/certificadotipo",
            sinOpcionInicial: true,
            requerido: true
          },
          {
            nombre: "crearautomaticamente",
            tipoitemformularioid: 6,
            etiqueta: "Crear automáticamente junto con un proceso de este tipo"
          },
          {
            nombre: "precio",
            tipoitemformularioid: 14,
            etiqueta: "Precio"
          },
          {
            nombre: "frecuenciaRefacturacion",
            tipoitemformularioid: 5,
            etiqueta: "Frecuencia de refacturación",
            opciones: FRECUENCIA_OPCIONES
          },
          {
            nombre: "templateDocumentoId",
            tipoitemformularioid: 5,
            etiqueta: "Template de documento al finalizar",
            opciones: "admin/template?dropdown=true",
            opcionVacia: true
          }
        ]
      },
      ...seccionesPago(null)
    ],
    botones: [
      {
        etiqueta: "Asignar",
        callback: crearAsignacion,
        validar: true
      }
    ]
  };

  function crearAsignacion(camposFormulario) {
    const { base, pagos } = separarPagos(camposFormulario);
    setLoaderCrear(true);
    accessAPI(
      "POST",
      `admin/actividad/asignaracertificadotipo/${actividadTipoId}`,
      { ...aplanarPrecioBase(base), pagos },
      (respuesta) => {
        setAsignaciones((prev) => [...prev, respuesta]);
        setLoaderCrear(false);
        setSideBarCrear(false);
        toast.current?.show({
          severity: "success",
          summary: "Asignado",
          detail: "Tipo de certificado asignado correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderCrear(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al asignar",
          sticky: true
        });
      }
    );
  }

  // Formulario de editar asignación
  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "crearautomaticamente",
              tipoitemformularioid: 6,
              etiqueta:
                "Crear automáticamente junto con un proceso de este tipo",
              respuesta: editando?.crearautomaticamente
            },
            {
              nombre: "precio",
              tipoitemformularioid: 14,
              etiqueta: "Precio",
              respuesta: {
                precio: editando?.precio ?? null,
                monedaId: editando?.moneda?.id ?? null
              }
            },
            {
              nombre: "frecuenciaRefacturacion",
              tipoitemformularioid: 5,
              etiqueta: "Frecuencia de refacturación",
              opciones: FRECUENCIA_OPCIONES,
              respuesta: editando?.frecuenciaRefacturacion || "NINGUNA"
            },
            {
              nombre: "templateDocumentoId",
              tipoitemformularioid: 5,
              etiqueta: "Template de documento al finalizar",
              opciones: "admin/template?dropdown=true",
              opcionVacia: true,
              respuesta: editando?.templateDocumento?.id || null
            }
          ]
        },
        ...seccionesPago(editando?.pagosColaboradores)
      ],
      botones: [
        {
          etiqueta: "Guardar",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function guardarEdicion(camposFormulario) {
    const { base, pagos } = separarPagos(camposFormulario);
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/actividad/asignaracertificadotipo/${editando.id}`,
      { ...aplanarPrecioBase(base), pagos },
      (respuesta) => {
        setAsignaciones((prev) =>
          prev.map((a) => (a.id === editando.id ? respuesta : a))
        );
        setLoaderEditar(false);
        setSideBarEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Asignación actualizada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar",
          sticky: true
        });
      }
    );
  }

  function eliminarAsignacion(asignacionId) {
    accessAPI(
      "DELETE",
      `admin/actividad/asignaracertificadotipo/${asignacionId}`,
      null,
      () => {
        setAsignaciones((prev) => prev.filter((a) => a.id !== asignacionId));
        toast.current?.show({
          severity: "success",
          summary: "Eliminado",
          detail: "Asignación eliminada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar",
          sticky: true
        });
      }
    );
  }

  function abrirCrear() {
    setFormCrearKey((prev) => prev + 1);
    setSideBarCrear(true);
  }

  function abrirEditar(asignacion) {
    setEditando(asignacion);
    setFormEditarKey((prev) => prev + 1);
    setSideBarEditar(true);
  }

  const getMenuItems = () => [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        if (menuSeleccionado) {
          abrirEditar(menuSeleccionado);
        }
      }
    },
    { separator: true },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (menuSeleccionado) {
          confirmDialog({
            message: `¿Está seguro que desea eliminar la asignación a "${menuSeleccionado.certificadotipo?.procesotipo?.nombre} - ${menuSeleccionado.certificadotipo?.nombre}"?`,
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Eliminar",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () => eliminarAsignacion(menuSeleccionado.id)
          });
        }
      }
    }
  ];

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={getMenuItems()} popup ref={menuRef} />
      <Tooltip target=".tt-icono" />


      {/* Sidebar crear */}
      <Sidebar
        visible={sideBarCrear}
        onHide={() => setSideBarCrear(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Asignar a tipo de certificado</Titulo>
          <Formulario
            key={formCrearKey}
            config={crearFormConfig}
            loader={loaderCrear}
          />
        </div>
      </Sidebar>

      {/* Sidebar editar */}
      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Editar asignación</Titulo>
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        </div>
      </Sidebar>

      <Titulo
        botones={
          <Boton callback={abrirCrear} texto icono chico negro>
            <i className="pi pi-plus"></i>
          </Boton>
        }
      >
        Tipos de certificado
      </Titulo>
      <TablaFiltrable conFiltros={false}
        value={asignaciones}
        nombreArchivo="tipos_certificado_actividad"
        size="small"
        emptyMessage="No hay tipos de certificado asignados"
        paginator
        rows={10}
      >
        <Column
          header="Tipo de certificado"
          body={(rowData) =>
            `${rowData.certificadotipo?.procesotipo?.nombre} - ${rowData.certificadotipo?.nombre}`
          }
        />
        <Column
          body={(rowData) =>
            rowData.crearautomaticamente ? (
              <Tag severity="success" css={{ fontSize: "0.75rem" }}>
                auto
              </Tag>
            ) : null
          }
          style={{ width: "4rem", textAlign: "center" }}
        />
        <Column
          body={(rowData) =>
            rowData.precio ? (
              <span
                className="tt-icono"
                data-pr-tooltip={`Precio: ${rowData.moneda?.simbolo || ""} ${rowData.precio}${rowData.moneda?.nombre ? ` (${rowData.moneda.nombre})` : ""}`}
                data-pr-position="top"
              >
                <Tag severity="success" css={{ fontSize: "0.75rem" }}>
                  <i className="pi pi-dollar" />
                </Tag>
              </span>
            ) : null
          }
          style={{ width: "4rem", textAlign: "center" }}
        />
        <Column
          body={(rowData) =>
            rowData.pagosColaboradores?.length ? (
              <span
                className="tt-icono"
                data-pr-tooltip={`Pago a colaboradores configurado en ${rowData.pagosColaboradores.length} paso(s)`}
                data-pr-position="top"
              >
                <Tag severity="warning" css={{ fontSize: "0.75rem" }}>
                  <i className="pi pi-users" />
                </Tag>
              </span>
            ) : null
          }
          style={{ width: "4rem", textAlign: "center" }}
        />
        <Column
          body={(rowData) =>
            rowData.frecuenciaRefacturacion ? (
              <span
                className="tt-icono"
                data-pr-tooltip={`Refacturación: ${frecuenciaEtiqueta(rowData.frecuenciaRefacturacion)}`}
                data-pr-position="top"
              >
                <Tag severity="info" css={{ fontSize: "0.75rem" }}>
                  <i className="pi pi-clock" />
                </Tag>
              </span>
            ) : null
          }
          style={{ width: "4rem", textAlign: "center" }}
        />
        <Column
          body={(rowData) =>
            rowData.templateDocumento ? (
              <span
                className="tt-icono"
                data-pr-tooltip={`Documento al finalizar: ${rowData.templateDocumento.nombre}`}
                data-pr-position="top"
              >
                <Tag severity="secondary" css={{ fontSize: "0.75rem" }}>
                  <i className="pi pi-file" />
                </Tag>
              </span>
            ) : null
          }
          style={{ width: "4rem", textAlign: "center" }}
        />
        <Column
          body={(rowData) => (
            <Boton
              icono
              negro
              callback={(e) => {
                setMenuSeleccionado(rowData);
                menuRef.current.toggle(e);
              }}
            >
              <i className="pi pi-ellipsis-v" />
            </Boton>
          )}
          style={{ width: "3rem", textAlign: "center" }}
        />
      </TablaFiltrable>
    </>
  );
}
